// TraitementDiversManager.jsx — Redesign Premium matching other modules
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Search, Filter, Check, X, User, AlertCircle,
    FileText, ChevronDown, Download, MessageSquare, Loader, Eye, Clock,
} from "lucide-react";

import {
    getManagerAttestationInboxRequest,
    managerAttestationDecisionRequest,
    downloadAttestationRequestPdfRequest,
    clearAttestationsMessage,
} from "../../../../redux/actions/attestationsActions";
import useTreatmentDelegation from "./useTreatmentDelegation";

import "../TraitementStyles/TraitementNotesFrais.css";

const TraitementDiversManager = () => {
    const dispatch = useDispatch();

    /* ── Redux ──────────────────────────────────────────────── */
    const attState = useSelector((state) => state.attestations || {});
    const managerInbox = attState.managerInbox || [];
    const loading = !!attState.loading;
    const apiError = attState.error;
    const apiMessage = attState.message;

    /* ── Local state ─────────────────────────────────────────── */
    const [activeTab, setActiveTab] = useState("enCours");
    const [searchTerm, setSearchTerm] = useState("");
    const [showFilters, setShowFilters] = useState(false);

    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedDemande, setSelectedDemande] = useState(null);
    const [motifRejet, setMotifRejet] = useState("");

    const [filters, setFilters] = useState({
        typeAttestation: "",
        dateDebut: "",
        dateFin: "",
    });

    const delegation = useTreatmentDelegation({
        endpoint: "/api/attestations/manager/treatment-delegation",
        label: "d'attestations",
        onUpdated: () => dispatch(getManagerAttestationInboxRequest()),
    });

    useEffect(() => {
        dispatch(getManagerAttestationInboxRequest());
    }, [dispatch]);

    useEffect(() => {
        if (apiError || apiMessage) {
            const t = setTimeout(() => dispatch(clearAttestationsMessage()), 1500);
            return () => clearTimeout(t);
        }
    }, [apiError, apiMessage, dispatch]);

    /* ── Helpers ─────────────────────────────────────────────── */
    const formatDate = (d) => {
        if (!d) return "—";
        const dt = new Date(d);
        if (Number.isNaN(dt.getTime())) return d;
        return dt.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
    };

    const mapStatusToTab = (s) => {
        const v = String(s || "").toUpperCase();
        if (v === "PENDING_MANAGER") return "enCours";
        if (v === "PENDING_ADMIN" || v === "APPROVED") return "approuve";
        if (v === "REJECTED") return "rejete";
        return "enCours";
    };

    const demandes = useMemo(() => {
        return (managerInbox || []).map((d) => {
            const att = d.attestation || {};
            const statusRaw = String(d.status || d.statut).toUpperCase();
            const statut = mapStatusToTab(statusRaw);
            return {
                id: d.id,
                matricule: d.matricule || d.code || "—",
                collaborateur: d.fullName || d.nomComplet || `${d.employeeNom || ""} ${d.employeePrenom || ""}`.trim() || "—",
                poste: d.poste || d.jobTitle || d.posteNom || "—",
                departement: d.departement || d.department || d.departementNom || "—",
                typeAttestation: d.attestationTitre || att.titre || att.nom || "Attestation",
                couleurType: d.couleurType || att.couleur || "#3b82f6",
                dateDemande: d.createdAt || d.dateDemande || "",
                motif: d.motif || d.commentaire || "",
                attachments: d.attachments || d.documentsJoints || [],
                managerComment: d.managerComment || "",
                statut,
                statusRaw,
                canDownload: statusRaw === "APPROVED",
            };
        });
    }, [managerInbox]);

    const getStatutBadge = (statut, statusRaw) => {
        const config = {
            enCours: { label: "En attente", bg: "#fff3cd", color: "#856404" },
            approuve: {
                label: statusRaw === "PENDING_ADMIN" ? "Approuvé (RH en cours)" : "Approuvé",
                bg: "#d4edda",
                color: "#155724"
            },
            rejete: { label: "Rejeté", bg: "#f8d7da", color: "#721c24" },
        };
        const c = config[statut] || config.enCours;
        return (
            <span className="tdBadge" style={{ backgroundColor: c.bg, color: c.color }}>
                {c.label}
            </span>
        );
    };

    const filtered = useMemo(() => {
        let list = demandes.filter((d) => d.statut === activeTab);

        if (searchTerm) {
            const s = searchTerm.toLowerCase();
            list = list.filter((d) =>
                [d.collaborateur, d.matricule, d.typeAttestation].join(" ").toLowerCase().includes(s)
            );
        }

        if (filters.typeAttestation) list = list.filter(d => d.typeAttestation === filters.typeAttestation);
        if (filters.dateDebut) list = list.filter(d => String(d.dateDemande).slice(0, 10) >= filters.dateDebut);
        if (filters.dateFin) list = list.filter(d => String(d.dateDemande).slice(0, 10) <= filters.dateFin);

        return list;
    }, [demandes, activeTab, searchTerm, filters]);

    const tabs = useMemo(() => [
        { id: "enCours", label: "En attente", count: demandes.filter(d => d.statut === "enCours").length },
        { id: "approuve", label: "Traitées", count: demandes.filter(d => d.statut === "approuve" || d.statut === "rejete").length },
    ], [demandes]);

    /* Adapt tabs counts to match the two-tab approach if that's what manager view uses */
    const managerTabs = useMemo(() => {
        const enCoursCount = demandes.filter(d => d.statut === "enCours").length;
        const traiteCount = demandes.filter(d => d.statut === "approuve" || d.statut === "rejete").length;
        return [
            { id: "enCours", label: "En attente", count: enCoursCount },
            { id: "traite", label: "Traitées", count: traiteCount },
        ];
    }, [demandes]);

    // Use traite logic if activeTab is "traite"
    const finalFiltered = useMemo(() => {
        if (activeTab === "enCours") return demandes.filter(d => d.statut === "enCours");
        return demandes.filter(d => d.statut === "approuve" || d.statut === "rejete");
    }, [demandes, activeTab]);

    /* Actually, let's stick to the filtered logic with the tab toggle */
    const listToDisplay = useMemo(() => {
        let list = finalFiltered;
        if (searchTerm) {
            const s = searchTerm.toLowerCase();
            list = list.filter(d => [d.collaborateur, d.matricule, d.typeAttestation].join(" ").toLowerCase().includes(s));
        }
        if (filters.typeAttestation) list = list.filter(d => d.typeAttestation === filters.typeAttestation);
        if (filters.dateDebut) list = list.filter(d => String(d.dateDemande).slice(0, 10) >= filters.dateDebut);
        if (filters.dateFin) list = list.filter(d => String(d.dateDemande).slice(0, 10) <= filters.dateFin);
        return list;
    }, [finalFiltered, searchTerm, filters]);

    const typesAttestation = useMemo(() => [...new Set(demandes.map(d => d.typeAttestation).filter(Boolean))], [demandes]);

    /* ── Actions ─────────────────────────────────────────────── */
    const handleApprove = (id) => dispatch(managerAttestationDecisionRequest({ id, approved: true, comment: "" }));
    const handleRejectClick = (d) => { setSelectedDemande(d); setMotifRejet(""); setShowRejectModal(true); };
    const confirmReject = () => {
        if (!selectedDemande || !motifRejet.trim()) return;
        dispatch(managerAttestationDecisionRequest({ id: selectedDemande.id, approved: false, comment: motifRejet }));
        setShowRejectModal(false);
    };

    const handleDownload = (d) => dispatch(downloadAttestationRequestPdfRequest({ requestId: d.id, filename: `${d.typeAttestation}.pdf` }));

    /* ── Render ──────────────────────────────────────────────── */
    if (loading && demandes.length === 0) {
        return (
            <div className="tdPage">
                <div className="tdWrap" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
                    <Loader size={32} className="spin" />
                </div>
            </div>
        );
    }

    return (
        <div className="tnfPage">
            <div className="tnfWrap">
                {/* Header */}
                <div className="tnfHeader" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                    <div>
                        <h1>Traitement des demandes d'attestations</h1>
                        <p>Validez les demandes d'attestations des membres de votre équipe</p>
                    </div>
                    <div className="tavDelegationSetting">
                        <div>
                            <div className="tavDelegationSetting__title">Délégation Admin RH</div>
                            <div className="tavDelegationSetting__sub">Transmettre mon traitement des attestations à l'admin RH</div>
                        </div>
                        <label className="tavSwitch" title="Délégation Admin RH">
                            <input
                                type="checkbox"
                                checked={delegation.enabled}
                                disabled={delegation.loading}
                                onChange={(e) => delegation.toggle(e.target.checked)}
                            />
                            <span />
                        </label>
                    </div>
                </div>

                {/* Search + Filters */}
                <div className="tnfCard">
                    <div className="tnfControls">
                        <div className="tnfSearch">
                            <Search size={18} />
                            <input
                                type="text"
                                placeholder="Rechercher par matricule, nom, type..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <button type="button" className="tnfFilterBtn" onClick={() => setShowFilters(v => !v)}>
                            <Filter size={16} /> Filtres
                            <ChevronDown size={16} className={`tnfChevron ${showFilters ? "is-open" : ""}`} />
                        </button>
                    </div>

                    {showFilters && (
                        <div className="tnfFilters">
                            <div className="tnfField">
                                <label>Type d'attestation</label>
                                <select value={filters.typeAttestation} onChange={e => setFilters(p => ({ ...p, typeAttestation: e.target.value }))}>
                                    <option value="">Tous</option>
                                    {typesAttestation.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                            <div className="tnfField"><label>Après le</label><input type="date" value={filters.dateDebut} onChange={e => setFilters(p => ({ ...p, dateDebut: e.target.value }))} /></div>
                            <div className="tnfField"><label>Avant le</label><input type="date" value={filters.dateFin} onChange={e => setFilters(p => ({ ...p, dateFin: e.target.value }))} /></div>
                        </div>
                    )}
                </div>

                {/* Tabs + Table */}
                <div className="tnfCard tnfCard--noPad">
                    <div className="tnfTabs">
                        {managerTabs.map(tab => (
                            <button key={tab.id} type="button"
                                className={`tnfTab ${activeTab === tab.id ? "is-active" : (activeTab === "traite" && tab.id === "traite" ? "is-active" : "")}`}
                                onClick={() => setActiveTab(tab.id)}>
                                {tab.label}
                                <span className={`tnfCount ${activeTab === tab.id ? "is-active" : ""}`}>{tab.count}</span>
                            </button>
                        ))}
                    </div>

                    <div className="tnfTableWrap">
                        {listToDisplay.length === 0 ? (
                            <div className="tnfEmpty"><AlertCircle size={46} /><div>Aucune demande trouvée</div></div>
                        ) : (
                            <table className="tnfTable">
                                <thead>
                                    <tr>
                                        <th>Matricule</th>
                                        <th>Collaborateur</th>
                                        <th>Département</th>
                                        <th>Type d'attestation</th>
                                        <th>Date de la demande</th>
                                        <th>Statut</th>
                                        <th className="is-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {listToDisplay.map(d => (
                                        <tr key={d.id} onClick={() => { setSelectedDemande(d); setShowDetailsModal(true); }} style={{ cursor: "pointer" }}>
                                            <td><span className="tnfPrimary">{d.matricule}</span></td>
                                            <td>
                                                <div className="tnfPrimary">{d.collaborateur}</div>
                                                <div className="tnfSub">{d.poste}</div>
                                            </td>
                                            <td><span className="tnfMuted">{d.departement}</span></td>
                                            <td>
                                                <div className="tnfType">
                                                    <span className="tnfDot" style={{ backgroundColor: d.couleurType }} />
                                                    <span className="tnfPrimary">{d.typeAttestation}</span>
                                                </div>
                                            </td>
                                            <td><div className="tnfDate"><Clock size={14} />{formatDate(d.dateDemande)}</div></td>
                                            <td>{getStatutBadge(d.statut, d.statusRaw)}</td>
                                            <td className="is-right" onClick={e => e.stopPropagation()}>
                                                <div className="tdRowActions">
                                                    {activeTab === "enCours" ? (
                                                        <>
                                                            <button type="button" className="tnfActionBtn tnfActionBtn--ok" onClick={() => handleApprove(d.id)} title="Approuver"><Check size={16} /></button>
                                                            <button type="button" className="tnfActionBtn tnfActionBtn--no" onClick={() => handleRejectClick(d)} title="Refuser"><X size={16} /></button>
                                                            <button type="button" className="tnfActionBtn" onClick={() => { setSelectedDemande(d); setShowDetailsModal(true); }} title="Détails" style={{ background: "#eff6ff", color: "#3b82f6", border: "1px solid #bfdbfe" }}><Eye size={16} /></button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <button type="button" className="tnfDetailsBtn" onClick={() => { setSelectedDemande(d); setShowDetailsModal(true); }}>Détails</button>
                                                            {d.canDownload && <button type="button" className="tnfActionBtn tnfActionBtn--dl" onClick={() => handleDownload(d)} title="Télécharger"><Download size={16} /></button>}
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            {/* Details Modal */}
            {showDetailsModal && selectedDemande && (
                <div className="tdModal">
                    <div className="tdModal__panel" role="dialog" aria-modal="true">
                        <div className="tdModal__header">
                            <div className="tdModal__headLeft">
                                <div className="tdModal__icon"><FileText size={18} /></div>
                                <div><div className="tdModal__title">Détails de la demande</div><div className="tdModal__sub">{selectedDemande.typeAttestation}</div></div>
                            </div>
                            <button type="button" className="tdIconBtn" onClick={() => setShowDetailsModal(false)}><X size={18} /></button>
                        </div>
                        <div className="tdModal__body">
                            <div className="tdGrid2">
                                <div className="tdPanel">
                                    <div className="tdPanel__title">Collaborateur</div>
                                    <div className="tdKv"><span>Matricule</span><b>{selectedDemande.matricule}</b></div>
                                    <div className="tdKv"><span>Nom</span><b>{selectedDemande.collaborateur}</b></div>
                                    <div className="tdKv"><span>Poste</span><b>{selectedDemande.poste}</b></div>
                                </div>
                                <div className="tdPanel">
                                    <div className="tdPanel__title">Demande</div>
                                    <div className="tdKv"><span>Type</span><b>{selectedDemande.typeAttestation}</b></div>
                                    <div className="tdKv"><span>Soumise le</span><b>{formatDate(selectedDemande.dateDemande)}</b></div>
                                    <div className="tdKv"><span>Statut</span><b>{getStatutBadge(selectedDemande.statut, selectedDemande.statusRaw)}</b></div>
                                </div>
                                <div className="tdPanel tdPanel--full">
                                    <div className="tdPanel__title"><MessageSquare size={16} /> Motif de la demande</div>
                                    <div className="tdText">{selectedDemande.motif || "Aucun motif précisé"}</div>
                                </div>
                                {selectedDemande.managerComment && (
                                    <div className={`tdPanel tdPanel--full ${selectedDemande.statut === "approuve" ? "tdValidOk" : "tdValidNo"}`}>
                                        <div className="tdPanel__title">Décision Manager</div>
                                        <div className="tdRejectBox">
                                            <div className="tdRejectLabel">Commentaire</div>
                                            <div className="tdRejectText">{selectedDemande.managerComment}</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="tdModal__footer">
                            <button className="tdBtn tdBtn--ghost" onClick={() => setShowDetailsModal(false)}>Fermer</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Reject Modal */}
            {showRejectModal && selectedDemande && (
                <div className="tdModal">
                    <div className="tdModal__panel tdModal__panel--small" role="dialog" aria-modal="true">
                        <div className="tdModal__header">
                            <div className="tdModal__headLeft">
                                <div className="tdModal__icon tdModal__icon--danger"><AlertCircle size={18} /></div>
                                <div><div className="tdModal__title">Refuser la demande</div><div className="tdModal__sub">{selectedDemande.collaborateur}</div></div>
                            </div>
                            <button type="button" className="tdIconBtn" onClick={() => setShowRejectModal(false)}><X size={18} /></button>
                        </div>
                        <div className="tdModal__body">
                            <label className="tdLabel">Motif du rejet <span className="tdReq">*</span></label>
                            <textarea className="tdTextarea" rows={4} value={motifRejet} onChange={e => setMotifRejet(e.target.value)} placeholder="Précisez la raison du refus..." />
                        </div>
                        <div className="tdModal__footer">
                            <button className="tdBtn tdBtn--ghost" onClick={() => setShowRejectModal(false)}>Annuler</button>
                            <button className="tdBtn tdBtn--danger" disabled={!motifRejet.trim()} onClick={confirmReject}>Confirmer le rejet</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TraitementDiversManager;
